import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../Utils/toastContext';
import Dropdown from '../../../Components/CustomDropdown/customdropdown';
import ConfirmContinueButton from '../../../Components/ContinueButton/continue-button';
import ConfirmResetButton from '../../../Components/ContinueButton/reset-button';
import DocumentPreview from '../../Create-Investment/documentpreview';
import ReviewPopup from '../../../Components/onboarding/review-popup';
import LabelWithStatus from '../../../Components/onboarding/label-with-status';
import StatusButton from '../../../Components/onboarding/status-button';
import CustomDropdownWithFetch from '../../../Components/CustomDropdown/custom-dropdown-fetchAPI';
import ThemedDatePicker from '../ThemedDatePicker';
import {
  setRejectedFields,
  setStepStatus,
} from '../../../Redux/store/onboardingFormSlice';
import { clearFileIdsRedux } from '../../../Redux/store/userSlice';
import AdditionalDocumentSection from './AdditionalDocumentSection';
import {
  mapAdditionalDocumentTypeChoices,
  AUSTRALIAN_STATE_OPTIONS,
  emptyAdditionalDocumentValues,
  findAdditionalDocumentOption,
  getAdditionalDocumentFields,
  getAdditionalDocumentTypeKey,
  requiresBackFile,
} from './additionalDocumentSchema';

const EMPTY_FILE_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ADDITIONAL_DOCUMENT_PAYLOAD_KEY = 'additional_documents';

const DEFAULT_PHOTO_OPTIONS = [
  { value: 1, label: 'Passport' },
  { value: 2, label: "Driver's License" },
  { value: 3, label: 'State or Territory Photo ID' },
];

const DEFAULT_NON_PHOTO_OPTIONS = [
  { value: 1, label: 'Utility Bill' },
  { value: 2, label: 'Bank Statement' },
  { value: 3, label: 'Government Letter' },
];

/* Labels for the per-document rejected fields returned by the
   rejected-fields API, e.g. "photographic_documents.114.document_number". */
const REJECTED_DOCUMENT_FIELD_LABELS = {
  document_type_code: 'Document type',
  document_number: 'Document number',
  card_number: 'Card number',
  expiry_date: 'Expiry date',
  file: 'Document file',
  back_file: 'Back file',
  state_of_issue: 'State or Territory in Australia',
  country_of_issue_id: 'Country where issued',
};

const createReviewState = (value = null) => ({
  verification_id: null,
  value,
  status: 'pending',
  rejection_reason: null,
  rejection_comment: null,
  rejection_timestamp: null,
  rejected_by: null,
  comments: [],
});

const createEmptyAdditionalFile = () => createReviewState(null);
let additionalDocumentSequence = 0;
const createAdditionalDocument = (documentType = null) => ({
  id: `additional-document-${Date.now()}-${additionalDocumentSequence++}`,
  documentType,
  values: {},
  /* Per-field review states ({ status, comments, ... }) hydrated from
     the preview API — used to render the StatusButton per field. */
  fieldStates: {},
  errors: {},
  file: createEmptyAdditionalFile(),
  backFile: createEmptyAdditionalFile(),
});

const toReviewState = (value, fallback = null) => {
  if (
    value &&
    typeof value === 'object' &&
    ('value' in value || 'status' in value || 'verification_id' in value)
  ) {
    return { ...createReviewState(fallback), ...value };
  }
  return createReviewState(value ?? fallback);
};

const formatDate = (date) => {
  if (!date) return '';
  const validDate = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(validDate.getTime())) return '';
  return [
    validDate.getFullYear(),
    String(validDate.getMonth() + 1).padStart(2, '0'),
    String(validDate.getDate()).padStart(2, '0'),
  ].join('-');
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed;
  const fallback = new Date(`${value}T00:00:00`);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const getReviewValue = (value, fallback = '') => {
  const unwrapped = value?.value ?? value;
  if (unwrapped && typeof unwrapped === 'object') {
    return unwrapped.id ?? unwrapped.value ?? unwrapped.name ?? fallback;
  }
  return unwrapped ?? fallback;
};

const getReviewOptionValue = (value, fallback = '') => {
  const unwrapped = value?.value ?? value;
  if (unwrapped && typeof unwrapped === 'object') {
    return {
      id: unwrapped.id ?? unwrapped.value ?? null,
      name: unwrapped.name ?? unwrapped.label ?? String(unwrapped.id ?? ''),
    };
  }
  return unwrapped ?? fallback;
};

/* ------------------------------------------------------------------
   FIX: this used to be `fileState?.value ?? fileState ?? null`.

   A file field's review state is { value: null, status: 'pending', ... }
   when nothing is uploaded. With `??`, a null value fell THROUGH to
   the state object itself (which is truthy), so FileUploadBox believed
   a file existed and rendered the "Upload completed" box INSTEAD of
   the upload input — no file, no way to upload.

   Now: a real file must actually carry file_id / file_name, either in
   .value (uploaded this session) or on the state itself (hydrated from
   the preview API, where file fields are merged with rejection info).
   ------------------------------------------------------------------ */
const getFileValue = (fileState) => {
  if (!fileState || typeof fileState !== 'object') return fileState ?? null;
  if (
    fileState.value &&
    typeof fileState.value === 'object' &&
    (fileState.value.file_id || fileState.value.file_name)
  ) {
    return fileState.value;
  }
  if (fileState.file_id || fileState.file_name) return fileState;
  return null;
};

const safeFile = (fileState) => {
  const file = getFileValue(fileState);
  if (!file?.file_id) return null;
  return {
    file_id: file.file_id,
    ...(file.file_name ? { file_name: file.file_name } : {}),
  };
};

const safeId = (value) => {
  const unwrapped = value?.value ?? value;
  if (unwrapped && typeof unwrapped === 'object') {
    return unwrapped.id ?? unwrapped.value ?? null;
  }
  return unwrapped ?? null;
};

const optionFromValue = (value, options = []) => {
  const id = safeId(value);
  if (id === null || id === undefined || id === '') return {};
  return (
    options.find((option) => String(option.value) === String(id)) || {
      value: id,
      label: value?.value?.name ?? value?.name ?? String(id),
    }
  );
};

/* ------------------------------------------------------------------
   FIX (driving licence back file): a Driving Licence that lives in the
   additional documents (merged from photographic_documents in the
   preview API response) must keep its back file. The schema option we
   build for it (enum match or the raw-key fallback) does not always
   mark DRIVING_LICENCE as a "requires back file" type, so the back
   file coming from the response API was not set up on the row.
   ------------------------------------------------------------------ */
const isDrivingLicenceDocumentType = (documentType) =>
  String(
    documentType?.code ??
      documentType?.value ??
      getAdditionalDocumentTypeKey(documentType) ??
      '',
  )
    .toUpperCase()
    .includes('DRIVING_LICENCE');

const rawDocumentHasBackFile = (rawDocument) =>
  !!(rawDocument?.back_file && getFileValue(rawDocument.back_file));

const getPhotoTypeValue = (document, fallback) => {
  const code = document?.document_type_code || document?.document_type?.code;
  if (code === 'PASSPORT') return 1;
  if (code === 'DRIVING_LICENCE') return 2;
  const value = getReviewValue(document?.document_type, fallback);
  return Number(value) || fallback;
};

const FileUploadBox = ({
  label,
  required = false,
  fileState,
  inputRef,
  onChange,
  onDelete,
  onPreview,
  onStatusClick,
  disabled = false,
}) => {
  const file = getFileValue(fileState);
  /* Only lock when there IS a file — a stale 'approved' status on an
     empty field must not hide the upload control. */
  const isLocked = disabled || (!!file && fileState?.status === 'approved');

  return (
    <div className="mt-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-700">
          {label} {required && <span className="text-rose-500">*</span>}
        </span>
        {/* FIX: dynamic verification status from the API instead of the
            static "Needs correction" badge. */}
        {fileState && (
          <StatusButton
            status={fileState.status}
            count={(fileState.comments || []).length}
            openSection={() =>
              onStatusClick?.(fileState, label, fileState.value, '')
            }
          />
        )}
      </div>

      {!file ? (
        <label
          className={`flex min-h-[128px] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-4 py-5 text-center transition ${
            isLocked
              ? 'cursor-not-allowed border-slate-200 bg-slate-100 opacity-60'
              : 'border-slate-300 bg-slate-50/70 hover:border[#375AE7] hover:bg-[#375AE7]-50/50'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.png,.jpeg,.jpg,application/pdf,image/png,image/jpeg"
            className="hidden"
            disabled={isLocked}
            onChange={onChange}
          />
          <span className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-lg text-[#375AE7]">
            ↑
          </span>
          <span className="text-sm font-semibold text-slate-700">
            Click to upload
          </span>
          <span className="mt-1 text-xs text-slate-400">
            PDF, PNG or JPEG · less than 10 MB
          </span>
        </label>
      ) : (
        <div
          className={`flex items-center justify-between gap-3 rounded-2xl border px-3 py-3 ${
            fileState?.status === 'rejected'
              ? 'border-rose-300 bg-rose-50/70'
              : 'border-emerald-200 bg-emerald-50/70'
          }`}
        >
          <button
            type="button"
            className="min-w-0 text-left"
            onClick={() => onPreview?.(file)}
          >
            <span className="block truncate text-sm font-semibold text-slate-800 underline underline-offset-2">
              {file.file_name || 'Uploaded document'}
            </span>
            <span className="mt-1 block text-xs font-medium text-emerald-700">
              Upload completed
            </span>
          </button>
          {!isLocked && (
            <button
              type="button"
              onClick={onDelete}
              className="rounded-xl px-2 py-1 text-lg text-rose-500 hover:bg-rose-100"
              aria-label={`Remove ${label}`}
            >
              ×
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ReviewableInput = ({
  id,
  label,
  valueState,
  onChange,
  required = false,
  placeholder = '',
  type = 'text',
  disabled = false,
  onStatusClick,
}) => (
  <div className="mt-4">
    <LabelWithStatus
      id={id}
      label={label}
      required={required}
      fieldData={valueState}
      handleStatusClick={onStatusClick}
    />
    <input
      id={id}
      type={type}
      value={valueState?.value ?? ''}
      disabled={disabled || valueState?.status === 'approved'}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
      className={`mt-1 w-full rounded-xl border bg-white px-3.5 py-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-[#375AE7] focus:ring-4 focus:ring-[#375AE7]-100 ${
        valueState?.status === 'rejected'
          ? 'border-rose-400 ring-2 ring-rose-100'
          : 'border-slate-200'
      }`}
    />
  </div>
);

const UploadProof = ({ setCurrentStep }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const updateFieldStatus = useSelector(
    (state) => state.user.updateFieldStatus,
  );
  const individualAccountType = useSelector(
    (state) => state.form.individualAccountType,
  );
  const stepStatus = useSelector((state) => state.form.stepStatus || []);
  const rejectedFields = useSelector((state) => state.form.rejectedFields);
  const isInvitee = useSelector((state) => state.user.isInvitee);
  const isInviteeDirector = useSelector(
    (state) => state.user.isInviteeDirector,
  );
  const userStatus = useSelector((state) => state.user.userStatus);

  const proofStepIndex =
    !isInvitee && !isInviteeDirector && individualAccountType == 5 ? 3 : 2;
  const proofStatus = stepStatus?.[proofStepIndex]?.status;
  const isProofLocked = ['COMPLETED', 'APPROVED', 'IN_REVIEW'].includes(
    proofStatus,
  );

  const [loading, setLoading] = useState(false);
  const [applicantCountry, setApplicantCountry] = useState(null);
  const [photographicIdData, setPhotographicIdData] = useState(
    DEFAULT_PHOTO_OPTIONS,
  );
  const [photographicDocumentTypes, setPhotographicDocumentTypes] = useState(
    [],
  );
  const [nonPhotoData, setNonPhotoData] = useState(DEFAULT_NON_PHOTO_OPTIONS);
  const [stateData, setStateData] = useState(AUSTRALIAN_STATE_OPTIONS);
  const [countryCodeData, setCountryCodeData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [currentField, setCurrentField] = useState({});
  const [currentFieldName, setCurrentFieldName] = useState('');
  const [currentFieldValue, setCurrentFieldValue] = useState('');
  const [currentFieldType, setCurrentFieldType] = useState('');

  const [signatory, setSignatory] = useState(createReviewState(true));
  const [description, setDescription] = useState(createReviewState(''));
  const [fileProof, setFileProof] = useState(createReviewState(null));
  const fileProofRef = useRef(null);

  const [photoId, setPhotoId] = useState(1);
  const [photoIdFirst, setPhotoIdFirst] = useState(1);
  const [photoIdSecond, setPhotoIdSecond] = useState(2);
  /* Verification states for the document-type dropdowns (hydrated from
     document_type / non_photogenic_id_type in the preview response). */
  const [photoTypeState, setPhotoTypeState] = useState(createReviewState(1));
  const [photoTypeStateFirst, setPhotoTypeStateFirst] = useState(
    createReviewState(1),
  );
  const [photoTypeStateSecond, setPhotoTypeStateSecond] = useState(
    createReviewState(2),
  );
  const [nonPhotoTypeState, setNonPhotoTypeState] = useState(
    createReviewState(1),
  );
  const [photoIdOption, setPhotoIdOption] = useState(DEFAULT_PHOTO_OPTIONS[0]);
  const [photoIdFirstOption, setPhotoIdFirstOption] = useState(
    DEFAULT_PHOTO_OPTIONS[0],
  );
  const [photoIdSecondOption, setPhotoIdSecondOption] = useState(
    DEFAULT_PHOTO_OPTIONS[1],
  );

  const [filePhotographicId, setFilePhotographicId] = useState(
    createReviewState(null),
  );
  const [fileDriverFront, setFileDriverFront] = useState(
    createReviewState(null),
  );
  const [fileDriverBack, setFileDriverBack] = useState(createReviewState(null));
  const [filePhotographicIdFirst, setFilePhotographicIdFirst] = useState(
    createReviewState(null),
  );
  const [filePhotographicIdSecond, setFilePhotographicIdSecond] = useState(
    createReviewState(null),
  );
  const [fileDriverFrontFirst, setFileDriverFrontFirst] = useState(
    createReviewState(null),
  );
  const [fileDriverBackFirst, setFileDriverBackFirst] = useState(
    createReviewState(null),
  );
  const [fileDriverFrontSecond, setFileDriverFrontSecond] = useState(
    createReviewState(null),
  );
  const [fileDriverBackSecond, setFileDriverBackSecond] = useState(
    createReviewState(null),
  );

  const filePhotoRef = useRef(null);
  const fileFrontRef = useRef(null);
  const fileBackRef = useRef(null);
  const filePhotoFirstRef = useRef(null);
  const filePhotoSecondRef = useRef(null);
  const fileFrontFirstRef = useRef(null);
  const fileBackFirstRef = useRef(null);
  const fileFrontSecondRef = useRef(null);
  const fileBackSecondRef = useRef(null);

  const [documentNumber, setDocumentNumber] = useState(createReviewState(''));
  const [documentNumberSecond, setDocumentNumberSecond] = useState(
    createReviewState(''),
  );
  const [cardNumber, setCardNumber] = useState(createReviewState(''));
  const [cardNumberSecond, setCardNumberSecond] = useState(
    createReviewState(''),
  );
  const [expiryDate, setExpiryDate] = useState(createReviewState(''));
  const [expiryDateSecond, setExpiryDateSecond] = useState(
    createReviewState(''),
  );

  const [nonphotoId, setNonphotoId] = useState(1);
  const [nonphotoOption, setNonphotoOption] = useState(
    DEFAULT_NON_PHOTO_OPTIONS[0],
  );
  const [fileNonPhotographicId, setFileNonPhotographicId] = useState(
    createReviewState(null),
  );
  const fileNonPhotoRef = useRef(null);

  const [countryCode, setCountryCode] = useState(createReviewState(null));
  const [countryCodeFirst, setCountryCodeFirst] = useState(
    createReviewState(null),
  );
  const [countryCodeSecond, setCountryCodeSecond] = useState(
    createReviewState(null),
  );
  const [stateId, setStateId] = useState(createReviewState(null));
  const [stateIdFirst, setStateIdFirst] = useState(createReviewState(null));
  const [stateIdSecond, setStateIdSecond] = useState(createReviewState(null));

  const [additionalDocuments, setAdditionalDocuments] = useState([]);
  const [additionalDocumentTypes, setAdditionalDocumentTypes] = useState([]);
  const [maximumDocuments, setMaximumDocuments] = useState(5);
  /* serverId -> display name of the primary photographic documents,
     captured from the preview response so rejected-field chips can say
     "Passport · Document number" instead of a raw API path. */
  const [photographicDocNames, setPhotographicDocNames] = useState({});

  const fieldLabelMap = {
    country_of_issue_id: 'Country where issued',
    state_of_issue: 'State or Territory in Australia',
  };

  const selectedPrimaryDocumentTypeCodes = useMemo(() => {
    const values =
      applicantCountry == null
        ? [photoId, photoIdFirst, photoIdSecond]
        : applicantCountry == 14
          ? [photoId]
          : [photoIdFirst, photoIdSecond];
    return [
      ...new Set(
        values.map((value) =>
          Number(value) === 1 ? 'PASSPORT' : 'DRIVING_LICENCE',
        ),
      ),
    ];
  }, [applicantCountry, photoId, photoIdFirst, photoIdSecond]);

  const additionalDocumentOptions = useMemo(() => {
    const supportingOptions = additionalDocumentTypes.filter(
      (option) => option.enabled !== false,
    );
    const alternatePhotographicOptions = photographicDocumentTypes
      .filter(
        (option) =>
          option.enabled !== false &&
          !selectedPrimaryDocumentTypeCodes.includes(option.code),
      )
      .map((option) => ({
        ...option,
        label: `${option.label} (Photographic document)`,
      }));

    return [...alternatePhotographicOptions, ...supportingOptions].filter(
      (option, index, options) =>
        options.findIndex((item) => item.code === option.code) === index,
    );
  }, [
    additionalDocumentTypes,
    photographicDocumentTypes,
    selectedPrimaryDocumentTypeCodes,
  ]);

  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const response = await fetch(`${BASE_URL}${url}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('jwtToken');
        navigate('/login');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Unable to load onboarding data.');
    }
    return response.json();
  };

  const fileUploadNew = async (file) => {
    const token = localStorage.getItem('jwtToken');
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${BASE_URL}/api/v1/files/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('jwtToken');
        navigate('/login');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Unable to upload file.');
    }
    return response.json();
  };

  const checkFileNameExist = (selectedFile) => {
    const selectedName = selectedFile?.name?.trim().toLowerCase();
    if (!selectedName) return false;

    const fileStates = [
      fileProof,
      filePhotographicId,
      fileDriverFront,
      fileDriverBack,
      filePhotographicIdFirst,
      filePhotographicIdSecond,
      fileDriverFrontFirst,
      fileDriverBackFirst,
      fileDriverFrontSecond,
      fileDriverBackSecond,
      fileNonPhotographicId,
      ...additionalDocuments.flatMap((document) => [
        document.file,
        document.backFile,
      ]),
    ];

    return fileStates.some((state) => {
      const file = getFileValue(state);
      return file?.file_name?.trim().toLowerCase() === selectedName;
    });
  };

  const uploadFileToState = async (selectedFile, setter, inputRef) => {
    if (!selectedFile) return;
    if (!EMPTY_FILE_TYPES.includes(selectedFile.type)) {
      showToast('File must be PDF, PNG, or JPEG.', 'error');
      return;
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      showToast('File must be less than 10MB.', 'error');
      return;
    }
    if (checkFileNameExist(selectedFile)) {
      showToast(
        'A file with the same name has already been uploaded.',
        'error',
      );
      return;
    }

    setLoading(true);
    try {
      const response = await fileUploadNew(selectedFile);
      /* Some deployments report 200, others 201 — accept both */
      if (response.status !== 200 && response.status !== 201) {
        throw new Error(response.message || 'Unable to upload file.');
      }
      /* Never mark the field uploaded when the API returned no file */
      if (!response.data) {
        throw new Error(response.message || 'Upload did not return file data.');
      }
      setter((previous) => ({ ...(previous || {}), value: response.data }));
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
      if (inputRef?.current) inputRef.current.value = '';
    }
  };

  const clearFile = (setter, inputRef) => {
    if (typeof setter === 'function') setter(createReviewState(null));
    if (inputRef?.current) inputRef.current.value = '';
  };

  const handleStatusClick = (docData, fieldName, fieldValue, fieldType) => {
    setCurrentField(docData || {});
    setCurrentFieldName(fieldName || '');
    setCurrentFieldValue(fieldValue || '');
    setCurrentFieldType(fieldType || '');
    dispatch(clearFileIdsRedux());
    setShowPopup(true);
  };

  const handleFilePreview = (file) => {
    setCurrentDocument(file);
    setIsModalOpen(true);
  };

  /* StatusButton click coming from the additional documents section.
     Resolves the review state for the field (hydrated per-field state,
     or the front/back file state) and opens the review popup. */
  const handleAdditionalStatusClick = (document, fieldKey, fieldValue) => {
    if (!document) return;
    const state =
      fieldKey === 'file'
        ? document.file
        : fieldKey === 'backFile'
          ? document.backFile
          : document.fieldStates?.[fieldKey] ||
            createReviewState(fieldValue ?? '');
    handleStatusClick(state, fieldKey, fieldValue ?? '', '');
  };

  const getCountries = async (page, searchTerm) => {
    try {
      const size = 10;
      const response = await GetAPIcalls(
        `/api/v1/users/countries?page=${page}&size=${size}&search=${encodeURIComponent(
          searchTerm || '',
        )}`,
      );
      const items = (response.data?.countries || []).map((item) => ({
        value: item.id,
        label: item.name,
        risk_factor: item.risk_factor,
      }));
      setCountryCodeData((previous) =>
        page === 1 ? items : [...previous, ...items],
      );
      return {
        items,
        hasMore: items.length === size,
      };
    } catch (error) {
      showToast(error.message, 'error');
      return { items: [], hasMore: false };
    }
  };

  const getAllEnums = async () => {
    try {
      const response = await GetAPIcalls(
        '/api/v1/onboarding/customer/proof-documents/enums',
      );
      const data = response.data || {};
      if (data.photographic_document_type_choices?.length) {
        setPhotographicIdData(
          data.photographic_document_type_choices.map((item) => ({
            value: item.value,
            code: item.code,
            label: item.label,
          })),
        );
        setPhotographicDocumentTypes(
          mapAdditionalDocumentTypeChoices(
            data.photographic_document_type_choices,
          ),
        );
      }
      if (data.non_photogenic_id_type_choices?.length) {
        setNonPhotoData(
          data.non_photogenic_id_type_choices.map((item) => ({
            value: item.value,
            label: item.label,
          })),
        );
      }
      if (data.australian_state_choices?.length) {
        setStateData(
          data.australian_state_choices.map((item) => ({
            value: item.value,
            label: item.label,
          })),
        );
      }
      if (Array.isArray(data.additional_document_type_choices)) {
        setAdditionalDocumentTypes(
          mapAdditionalDocumentTypeChoices(
            data.additional_document_type_choices,
          ),
        );
      }
      if (data.maximum_documents) {
        setMaximumDocuments(Number(data.maximum_documents));
      }
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const getApplicantCountry = async () => {
    try {
      const response = await GetAPIcalls(
        '/api/v1/onboarding/applicant-country',
      );
      setApplicantCountry(response.data?.id ?? null);
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const setPhotoDocumentType = (selectedOption, position = 'primary') => {
    if (!selectedOption) return;
    const numericValue = Number(selectedOption.value);

    if (position === 'primary') {
      setPhotoId(numericValue);
      setPhotoIdOption(selectedOption);
      updateReview(setPhotoTypeState, numericValue);
      clearFile(setFilePhotographicId, filePhotoRef);
      clearFile(setFileDriverFront, fileFrontRef);
      clearFile(setFileDriverBack, fileBackRef);
      setDocumentNumber(createReviewState(''));
      setCardNumber(createReviewState(''));
      setExpiryDate(createReviewState(''));
      /* FIX (passport state_of_issue): the state field is hidden for
         passports — drop any previously selected state. */
      setStateId(createReviewState(null));
    }

    if (position === 'first') {
      if (numericValue === photoIdSecond) {
        showToast('Please select two different photographic IDs.', 'error');
        return;
      }
      setPhotoIdFirst(numericValue);
      setPhotoIdFirstOption(selectedOption);
      updateReview(setPhotoTypeStateFirst, numericValue);
      clearFile(setFilePhotographicIdFirst, filePhotoFirstRef);
      clearFile(setFileDriverFrontFirst, fileFrontFirstRef);
      clearFile(setFileDriverBackFirst, fileBackFirstRef);
      setDocumentNumber(createReviewState(''));
      setCardNumber(createReviewState(''));
      setExpiryDate(createReviewState(''));
      /* FIX (passport state_of_issue): the state field is hidden for
         passports — drop any previously selected state. */
      setStateIdFirst(createReviewState(null));
    }

    if (position === 'second') {
      if (numericValue === photoIdFirst) {
        showToast('Please select two different photographic IDs.', 'error');
        return;
      }
      setPhotoIdSecond(numericValue);
      setPhotoIdSecondOption(selectedOption);
      updateReview(setPhotoTypeStateSecond, numericValue);
      clearFile(setFilePhotographicIdSecond, filePhotoSecondRef);
      clearFile(setFileDriverFrontSecond, fileFrontSecondRef);
      clearFile(setFileDriverBackSecond, fileBackSecondRef);
      setDocumentNumberSecond(createReviewState(''));
      setCardNumberSecond(createReviewState(''));
      setExpiryDateSecond(createReviewState(''));
      /* FIX (passport state_of_issue): the state field is hidden for
         passports — drop any previously selected state. */
      setStateIdSecond(createReviewState(null));
    }
  };

  const selectCountry = (selectedOption, position = 'primary') => {
    const nextValue = selectedOption
      ? {
          id: selectedOption.value,
          name: selectedOption.label,
          risk_factor: selectedOption.risk_factor,
        }
      : null;
    if (position === 'primary') {
      setCountryCode((previous) => ({ ...previous, value: nextValue }));
      if (nextValue?.id !== 14) setStateId(createReviewState(null));
    }
    if (position === 'first') {
      setCountryCodeFirst((previous) => ({ ...previous, value: nextValue }));
      if (nextValue?.id !== 14) setStateIdFirst(createReviewState(null));
    }
    if (position === 'second') {
      setCountryCodeSecond((previous) => ({ ...previous, value: nextValue }));
      if (nextValue?.id !== 14) setStateIdSecond(createReviewState(null));
    }
  };

  const selectState = (selectedOption, position = 'primary') => {
    const value = selectedOption
      ? { id: selectedOption.value, name: selectedOption.label }
      : null;
    if (position === 'primary') {
      setStateId((previous) => ({ ...previous, value }));
    }
    if (position === 'first') {
      setStateIdFirst((previous) => ({ ...previous, value }));
    }
    if (position === 'second') {
      setStateIdSecond((previous) => ({ ...previous, value }));
    }
  };

  const addAdditionalDocument = () => {
    const primaryCount = applicantCountry === 14 ? 1 : 2;
    if (primaryCount + additionalDocuments.length >= maximumDocuments) {
      showToast(
        'No more than five identity documents may be uploaded.',
        'error',
      );
      return;
    }
    setAdditionalDocuments((previous) => [
      ...previous,
      createAdditionalDocument(),
    ]);
  };

  const removeAdditionalDocument = (documentId) => {
    if (!documentId) return;
    setAdditionalDocuments((previous) =>
      (previous || []).filter((document) => document?.id !== documentId),
    );
  };

  const clearAdditionalDocumentFile = (documentId, side = 'front') => {
    if (!documentId) return;
    const fieldName = side === 'back' ? 'backFile' : 'file';
    setAdditionalDocuments((previous) =>
      (previous || []).map((document) =>
        document?.id === documentId
          ? { ...document, [fieldName]: createEmptyAdditionalFile() }
          : document,
      ),
    );
  };

  const clearAdditionalDocument = () => {
    setAdditionalDocuments([]);
  };

  const handleSelectAdditionalDocumentType = (documentId, selectedOption) => {
    if (!selectedOption) return;
    const selectedKey = getAdditionalDocumentTypeKey(selectedOption);
    const isAllowed = additionalDocumentOptions.some(
      (option) => getAdditionalDocumentTypeKey(option) === selectedKey,
    );
    const alreadyUsed = additionalDocuments.some(
      (document) =>
        document.id !== documentId &&
        getAdditionalDocumentTypeKey(document.documentType) === selectedKey,
    );

    if (!isAllowed || alreadyUsed) {
      showToast(
        'This document type is already selected. Please choose a different document type.',
        'error',
      );
      return;
    }

    setAdditionalDocuments((previous) =>
      previous.map((document) =>
        document.id === documentId
          ? {
              ...document,
              documentType: selectedOption,
              values: emptyAdditionalDocumentValues(selectedOption),
              errors: {},
              file: createEmptyAdditionalFile(),
              backFile: createEmptyAdditionalFile(),
            }
          : document,
      ),
    );
  };

  const handleAdditionalFieldChange = (documentId, fieldName, value) => {
    setAdditionalDocuments((previous) =>
      previous.map((document) =>
        document.id === documentId
          ? {
              ...document,
              values: { ...document.values, [fieldName]: value },
              errors: { ...document.errors, [fieldName]: undefined },
            }
          : document,
      ),
    );
  };

  const handleAdditionalFileSelected = async (documentId, side, file) => {
    const fieldName = side === 'back' ? 'backFile' : 'file';
    const setFileForDocument = (updater) => {
      setAdditionalDocuments((previous) =>
        previous.map((document) => {
          if (document.id !== documentId) return document;
          const currentFile = document[fieldName];
          const nextFile =
            typeof updater === 'function' ? updater(currentFile) : updater;
          return { ...document, [fieldName]: nextFile };
        }),
      );
    };

    await uploadFileToState(file, setFileForDocument, null);
  };

  useEffect(() => {
    /* FIX (driving licence back file): the preview response can hydrate
       a driving licence into the additional documents before the enums
       endpoint has responded. With an empty options list nothing can be
       identified as a duplicate yet — skip and re-check when the
       options arrive, otherwise the hydrated row is wrongly removed. */
    if (additionalDocumentOptions.length === 0) return;
    const conflictingDocuments = additionalDocuments.filter(
      (document) =>
        document.documentType?.value &&
        !additionalDocumentOptions.some(
          (option) =>
            getAdditionalDocumentTypeKey(option) ===
            getAdditionalDocumentTypeKey(document.documentType),
        ),
    );

    if (conflictingDocuments.length > 0) {
      const conflictingIds = new Set(
        conflictingDocuments.map((document) => document.id),
      );
      setAdditionalDocuments((previous) =>
        previous.filter((document) => !conflictingIds.has(document.id)),
      );
      showToast(
        'A duplicate primary document was removed from the additional documents.',
        'error',
      );
    }
  }, [additionalDocumentOptions, additionalDocuments]);

  const validateAdditionalDocument = () => {
    const selectedDocuments = additionalDocuments.filter(
      (document) => document.documentType?.value || document.documentType?.code,
    );
    const primaryCount = applicantCountry === 14 ? 1 : 2;

    if (primaryCount + selectedDocuments.length > maximumDocuments) {
      showToast(
        'No more than five identity documents may be uploaded.',
        'error',
      );
      return false;
    }

    if (selectedDocuments.length === 0) return true;

    const errorsById = {};

    for (const document of selectedDocuments) {
      const typeKey = getAdditionalDocumentTypeKey(document.documentType);
      const fields = getAdditionalDocumentFields(document.documentType);
      const errors = {};

      fields.forEach((fieldDefinition) => {
        if (
          fieldDefinition.required &&
          !String(document.values?.[fieldDefinition.name] ?? '').trim()
        ) {
          errors[fieldDefinition.name] =
            `${fieldDefinition.label} is required.`;
        }
      });

      const anyOfFields = fields.filter(
        (fieldDefinition) => fieldDefinition.requiredGroup === 'any_of_fields',
      );
      if (
        anyOfFields.length > 0 &&
        !anyOfFields.some((fieldDefinition) =>
          String(document.values?.[fieldDefinition.name] ?? '').trim(),
        )
      ) {
        anyOfFields.forEach((fieldDefinition) => {
          errors[fieldDefinition.name] =
            'At least one of these fields is required.';
        });
      }

      if (typeKey === 'death_certificate') {
        const registrationNumber = String(
          document.values?.registrationNumber ?? '',
        ).trim();
        const certificateNumber = String(
          document.values?.certificateNumber ?? '',
        ).trim();
        if (!registrationNumber && !certificateNumber) {
          errors.registrationNumber =
            'Enter a registration number or certificate number.';
          errors.certificateNumber =
            'Enter a registration number or certificate number.';
        }
      }

      if (Object.keys(errors).length > 0) {
        errorsById[document.id] = errors;
      }
    }

    if (Object.keys(errorsById).length > 0) {
      setAdditionalDocuments((previous) =>
        previous.map((document) => ({
          ...document,
          errors: errorsById[document.id] || {},
        })),
      );
      const firstError = Object.values(errorsById)[0];
      showToast(Object.values(firstError)[0], 'error');
      return false;
    }

    /* Use getFileValue so hydrated files (file fields on the state
       object, value: null) still count as uploaded. */
    const missingFrontFile = selectedDocuments.find(
      (document) => !getFileValue(document.file),
    );
    if (missingFrontFile) {
      showToast(
        `Please upload document ${additionalDocuments.indexOf(missingFrontFile) + 1}.`,
        'error',
      );
      return false;
    }

    const missingBackFile = selectedDocuments.find(
      (document) =>
        (requiresBackFile(document.documentType) ||
          isDrivingLicenceDocumentType(document.documentType)) &&
        !getFileValue(document.backFile),
    );
    if (missingBackFile) {
      showToast(
        `Please upload the back side of document ${additionalDocuments.indexOf(missingBackFile) + 1}.`,
        'error',
      );
      return false;
    }

    return true;
  };

  const buildAdditionalDocumentPayload = (document) => {
    if (!document?.documentType?.value && !document?.documentType?.code)
      return null;
    const definitions = getAdditionalDocumentFields(document.documentType);
    const payload = {
      ...(document.serverId ? { id: document.serverId } : {}),
      document_type_code:
        document.documentType.code || document.documentType.value,
      file: safeFile(document.file),
      ...(requiresBackFile(document.documentType) ||
      isDrivingLicenceDocumentType(document.documentType)
        ? { back_file: safeFile(document.backFile) }
        : {}),
    };
    definitions.forEach((definition) => {
      const value = document.values?.[definition.name];
      if (value === undefined || value === null || value === '') return;
      const isLocationField =
        definition.name.includes('country_') ||
        definition.name.includes('state_') ||
        definition.name === 'state';
      /* FIX: all country fields must be sent with the _id suffix the
         API expects, e.g. country_of_issue -> country_of_issue_id
         (same as buildPhotoPayload). country_of_issue_id maps to
         itself, so both the enum name and the raw preview key work. */
      const payloadFieldName = definition.name.includes('country_of_issue')
        ? 'country_of_issue_id'
        : definition.name.includes('country_of_birth')
          ? 'country_of_birth_id'
          : definition.name;
      payload[payloadFieldName] = isLocationField
        ? safeId(value)
        : definition.type === 'number'
          ? Number(value)
          : value;
    });
    return payload;
  };

  const buildAdditionalDocumentsPayload = () =>
    additionalDocuments
      .filter(
        (document) =>
          document.documentType?.value || document.documentType?.code,
      )
      .map(buildAdditionalDocumentPayload)
      .filter(Boolean);

  const buildPhotoPayload = ({
    documentType,
    file,
    backFile,
    expiry,
    documentNo,
    cardNo,
    country,
    state,
  }) => ({
    document_type_code:
      Number(documentType) === 1 ? 'PASSPORT' : 'DRIVING_LICENCE',
    file: safeFile(file),
    ...(backFile ? { back_file: safeFile(backFile) } : {}),
    expiry_date: formatDate(expiry),
    document_number: documentNo || null,
    ...(cardNo ? { card_number: cardNo } : {}),
    country_of_issue_id: safeId(country),
    /* FIX (passport state_of_issue): never send a state for passports —
       a stale value from a previously selected document type must not
       leak into the payload. */
    state_of_issue: Number(documentType) === 1 ? null : safeId(state),
  });

  const validatePhotoDocument = ({
    documentType,
    file,
    backFile,
    documentNo,
    expiry,
    country,
    state,
    label,
  }) => {
    if (!country?.value?.id) {
      showToast(`Please select the issuing country for ${label}.`, 'error');
      return false;
    }
    /* FIX (passport state_of_issue): a passport has no Australian state of
       issue — only require the state for non-passport documents. */
    if (
      Number(documentType) !== 1 &&
      country.value.id === 14 &&
      !state?.value?.id
    ) {
      showToast(`Please select the Australian state for ${label}.`, 'error');
      return false;
    }
    /* getFileValue handles both wrapped (value) and hydrated
       (merged onto the state) file shapes. */
    if (!getFileValue(file)) {
      showToast(`Please upload ${label}.`, 'error');
      return false;
    }
    if (documentType === 2 && !getFileValue(backFile)) {
      showToast(`Please upload the back side of ${label}.`, 'error');
      return false;
    }
    if (!documentNo?.value) {
      showToast(`Please enter the document number for ${label}.`, 'error');
      return false;
    }
    if (!expiry?.value) {
      showToast(`Please select the expiry date for ${label}.`, 'error');
      return false;
    }
    return true;
  };

  const handleContinue = (isFinal) => {
    if (isFinal) {
      if (!validateAdditionalDocument()) return;
      if (signatory.value === true) {
        if (!description.value) {
          showToast('Please enter the prosecution description.', 'error');
          return;
        }
        if (!getFileValue(fileProof)) {
          showToast('Please upload prosecution proof.', 'error');
          return;
        }
      }

      if (applicantCountry === 14) {
        const primaryValid = validatePhotoDocument({
          documentType: photoId,
          file: photoId === 2 ? fileDriverFront : filePhotographicId,
          backFile: photoId === 2 ? fileDriverBack : null,
          documentNo: documentNumber,
          expiry: expiryDate,
          country: countryCode,
          state: stateId,
          label: photoId === 2 ? "the driver's licence" : 'the photographic ID',
        });
        if (!primaryValid) return;

        if (photoId !== 2) {
          if (!getFileValue(fileNonPhotographicId)) {
            showToast('Please upload a non-photographic ID.', 'error');
            return;
          }
        }
      }

      if (applicantCountry !== 14) {
        const firstValid = validatePhotoDocument({
          documentType: photoIdFirst,
          file:
            photoIdFirst === 2 ? fileDriverFrontFirst : filePhotographicIdFirst,
          backFile: photoIdFirst === 2 ? fileDriverBackFirst : null,
          documentNo: documentNumber,
          expiry: expiryDate,
          country: countryCodeFirst,
          state: stateIdFirst,
          label: 'the first photographic ID',
        });
        if (!firstValid) return;

        const secondValid = validatePhotoDocument({
          documentType: photoIdSecond,
          file:
            photoIdSecond === 2
              ? fileDriverFrontSecond
              : filePhotographicIdSecond,
          backFile: photoIdSecond === 2 ? fileDriverBackSecond : null,
          documentNo: documentNumberSecond,
          expiry: expiryDateSecond,
          country: countryCodeSecond,
          state: stateIdSecond,
          label: 'the second photographic ID',
        });
        if (!secondValid) return;

        if (
          photoIdFirst !== 2 &&
          photoIdSecond !== 2 &&
          !getFileValue(fileNonPhotographicId)
        ) {
          showToast('Please upload a non-photographic ID.', 'error');
          return;
        }
      }
    }

    setLoading(true);
    saveApplication(isFinal);
  };

  const submitApplicationAPI = async (isFinal) => {
    const token = localStorage.getItem('jwtToken');
    const isOnboarding = userStatus === 'onboarding';
    const apiUrl = isInvitee
      ? isOnboarding
        ? `${BASE_URL}/api/v1/onboarding/invited-user/proof-documents/submit`
        : `${BASE_URL}/api/v1/onboarding/invited-user/proof-documents/update`
      : isOnboarding
        ? `${BASE_URL}/api/v1/onboarding/customer/proof-documents/submit`
        : `${BASE_URL}/api/v1/onboarding/customer/proof-documents/update`;

    const payload = {
      is_submitted: Boolean(isFinal),
      has_prosecution_history: Boolean(signatory.value),
      prosecution_description: signatory.value
        ? description.value || null
        : null,
      prosecution_file: signatory.value ? safeFile(fileProof) : null,
    };

    if (applicantCountry === 14) {
      payload.photographic_documents = [
        buildPhotoPayload({
          documentType: photoId,
          file: photoId === 2 ? fileDriverFront : filePhotographicId,
          backFile: photoId === 2 ? fileDriverBack : null,
          expiry: expiryDate.value,
          documentNo: documentNumber.value,
          cardNo: cardNumber.value,
          country: countryCode.value,
          state: stateId.value,
        }),
      ];
      payload.non_photogenic_id_type = photoId === 2 ? null : nonphotoId;
      payload.non_photogenic_file =
        photoId === 2 ? null : safeFile(fileNonPhotographicId);
    } else {
      payload.photographic_documents = [
        buildPhotoPayload({
          documentType: photoIdFirst,
          file:
            photoIdFirst === 2 ? fileDriverFrontFirst : filePhotographicIdFirst,
          backFile: photoIdFirst === 2 ? fileDriverBackFirst : null,
          expiry: expiryDate.value,
          documentNo: documentNumber.value,
          cardNo: cardNumber.value,
          country: countryCodeFirst.value,
          state: stateIdFirst.value,
        }),
        buildPhotoPayload({
          documentType: photoIdSecond,
          file:
            photoIdSecond === 2
              ? fileDriverFrontSecond
              : filePhotographicIdSecond,
          backFile: photoIdSecond === 2 ? fileDriverBackSecond : null,
          expiry: expiryDateSecond.value,
          documentNo: documentNumberSecond.value,
          cardNo: cardNumberSecond.value,
          country: countryCodeSecond.value,
          state: stateIdSecond.value,
        }),
      ];
      payload.non_photogenic_id_type =
        photoIdFirst === 2 || photoIdSecond === 2 ? null : nonphotoId;
      payload.non_photogenic_file =
        photoIdFirst === 2 || photoIdSecond === 2
          ? null
          : safeFile(fileNonPhotographicId);
    }

    const documentPayloads = buildAdditionalDocumentsPayload();
    const additionalPhotographicDocuments = documentPayloads.filter(
      (document) =>
        document.document_type_code === 'PASSPORT' ||
        document.document_type_code === 'DRIVING_LICENCE',
    );
    const supportingDocuments = documentPayloads.filter(
      (document) =>
        document.document_type_code !== 'PASSPORT' &&
        document.document_type_code !== 'DRIVING_LICENCE',
    );

    payload.photographic_documents = [
      ...(payload.photographic_documents || []),
      ...additionalPhotographicDocuments,
    ];
    payload[ADDITIONAL_DOCUMENT_PAYLOAD_KEY] = supportingDocuments;

    const response = await fetch(apiUrl, {
      method: isOnboarding ? 'POST' : 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('jwtToken');
        navigate('/login');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          errorData.error ||
          Object.values(errorData.errors || {})
            .flat()
            .filter(Boolean)
            .join(' · ') ||
          'Unable to save proof documents.',
      );
    }
    return response.json();
  };

  const refreshOnboardingState = async () => {
    try {
      const statusResponse = await GetAPIcalls('/api/v1/onboarding/status');
      if (statusResponse.status === 200 && statusResponse.data?.length) {
        dispatch(setStepStatus(statusResponse.data));
      }
      const rejectedResponse = await GetAPIcalls(
        '/api/v1/onboarding/rejected-fields',
      );
      if (rejectedResponse.status === 200) {
        dispatch(setRejectedFields(rejectedResponse.data));
      }
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const saveApplication = async (isFinal) => {
    try {
      const response = await submitApplicationAPI(isFinal);
      if (response.status !== 200) {
        throw new Error(response.message || 'Unable to save proof documents.');
      }
      await getDetails();
      if (isFinal) {
        setCurrentStep(
          !isInvitee && !isInviteeDirector && individualAccountType == 5
            ? 5
            : 4,
        );
        await refreshOnboardingState();
      }
      showToast(
        response.message ||
          (isFinal
            ? 'Proof documents submitted successfully.'
            : 'Information saved as draft.'),
        'success',
      );
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetApplicationAPI = async () => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = isInvitee
      ? `${BASE_URL}/api/v1/onboarding/invited-user/proof-documents/reset`
      : `${BASE_URL}/api/v1/onboarding/customer/proof-documents/reset`;
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Unable to reset application.');
    }
    return response.json();
  };

  const handleReset = async () => {
    setLoading(true);
    try {
      const response = await resetApplicationAPI();
      if (response.status !== 200) {
        throw new Error(response.message || 'Unable to reset application.');
      }
      clearAdditionalDocument();
      await refreshOnboardingState();
      showToast(
        response.message || 'Application reset successfully.',
        'success',
      );
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const hydrateAdditionalDocuments = (rawDocuments) => {
    const documents = Array.isArray(rawDocuments)
      ? rawDocuments
      : rawDocuments
        ? [rawDocuments]
        : [];

    if (documents.length === 0) {
      clearAdditionalDocument();
      return;
    }

    const hydratedDocuments = documents.map((rawDocument) => {
      const rawTypeCode = getReviewValue(
        rawDocument.document_type_code ||
          rawDocument.document_type?.code ||
          rawDocument.document_type,
        '',
      );
      const source =
        rawDocument.fields || rawDocument.document_fields || rawDocument;
      const matchedOption = findAdditionalDocumentOption(
        { code: rawTypeCode },
        [...additionalDocumentTypes, ...photographicDocumentTypes],
      );
      const fallbackFieldNames = Object.keys(source).filter(
        (fieldName) =>
          ![
            'id',
            'document_type',
            'document_type_code',
            'document_type_name',
            'file',
            'back_file',
          ].includes(fieldName) && !fieldName.endsWith('_name'),
      );
      const option = matchedOption || {
        value: rawTypeCode,
        code: rawTypeCode,
        label:
          getReviewValue(rawDocument.document_type_name, '') ||
          String(rawTypeCode),
        enumItem: {
          required_fields: fallbackFieldNames,
          any_of_fields: [],
          field_choices: {},
        },
      };
      const fields = getAdditionalDocumentFields(option);
      const hydratedFields = fields.length
        ? fields
        : fallbackFieldNames.map((fieldName) => ({
            name: fieldName,
            label: fieldName.replace(/_/g, ' '),
            type: fieldName.endsWith('_date') ? 'date' : 'text',
            required: false,
            options: [],
          }));
      const fieldStates = {};
      const values = hydratedFields.reduce((result, fieldDefinition) => {
        const fieldName = fieldDefinition.name;
        const sourceValue =
          source[fieldName] ??
          (fieldName.includes('country_of_issue')
            ? source.country_of_issue
            : fieldName.includes('country_of_birth')
              ? source.country_of_birth
              : fieldName.includes('state_of_issue')
                ? source.state_of_issue
                : fieldName.includes('registration_state')
                  ? source.registration_state
                  : fieldName === 'state'
                    ? source.state
                    : undefined);
        const isLocationField =
          fieldName.includes('country_') ||
          fieldName.includes('state_') ||
          fieldName === 'state';
        /* Keep the full review state (status + comments) so the
           StatusButton can be rendered per field, exactly like the
           primary photographic-document fields. */
        fieldStates[fieldName] = toReviewState(sourceValue, '');
        result[fieldName] = isLocationField
          ? getReviewOptionValue(sourceValue, '')
          : getReviewValue(sourceValue, '');
        return result;
      }, {});

      const baseDocumentType = fields.length
        ? option
        : {
            ...option,
            enumItem: {
              ...(option.enumItem || {}),
              required_fields: fallbackFieldNames,
              any_of_fields: [],
              field_choices: {},
            },
          };

      /* FIX (driving licence back file): flag a driving licence — or any
         document that actually carries a back file in the API response —
         with the requiresBackFile flags the schema/section read, so the
         back file hydrated from the preview response is shown and kept
         on the row instead of being hidden/dropped. */
      const needsBackFileSide =
        isDrivingLicenceDocumentType(baseDocumentType) ||
        rawDocumentHasBackFile(rawDocument);
      const hydratedDocumentType = needsBackFileSide
        ? {
            ...baseDocumentType,
            requiresBackFile: true,
            enumItem: {
              ...(baseDocumentType.enumItem || {}),
              requires_back_file: true,
            },
          }
        : baseDocumentType;

      return {
        ...createAdditionalDocument(hydratedDocumentType),
        serverId: safeId(rawDocument.id) ?? null,
        values,
        fieldStates,
        file: toReviewState(rawDocument.file, null),
        backFile: toReviewState(rawDocument.back_file, null),
      };
    });

    setAdditionalDocuments(hydratedDocuments);
  };

  const getDetails = async () => {
    try {
      const response = await GetAPIcalls(
        isInvitee
          ? '/api/v1/onboarding/invited-user/proof-documents/verification/preview'
          : '/api/v1/onboarding/customer/proof-documents/verification/preview',
      );
      const data = response.data;
      if (!data) {
        clearAdditionalDocument();
        return;
      }

      hydrateAdditionalDocuments(data.additional_documents || []);
      if (data.has_prosecution_history !== undefined) {
        setSignatory(toReviewState(data.has_prosecution_history, false));
      }
      if (data.prosecution_description !== undefined) {
        setDescription(toReviewState(data.prosecution_description, ''));
      }
      if (data.prosecution_file !== undefined) {
        setFileProof(toReviewState(data.prosecution_file, null));
      }

      const documents = data.photographic_documents || [];
      if (data.applicant_country?.value?.id === 14 && documents[0]) {
        const document = documents[0];
        const type = getPhotoTypeValue(document, 1);
        const option = optionFromValue({ value: type }, photographicIdData);
        setPhotoId(type);
        setPhotoIdOption(option);
        setPhotoTypeState(toReviewState(document.document_type, type));
        setPhotographicDocNames((previous) => ({
          ...previous,
          [String(safeId(document.id) ?? '')]:
            getReviewValue(document.document_type_name, '') ||
            'Photographic document',
        }));
        setCountryCode(toReviewState(document.country_of_issue, null));
        setStateId(toReviewState(document.state_of_issue, null));
        setDocumentNumber(toReviewState(document.document_number, ''));
        setCardNumber(toReviewState(document.card_number, ''));
        setExpiryDate(toReviewState(document.expiry_date, ''));
        if (type === 2) {
          setFileDriverFront(toReviewState(document.file, null));
          setFileDriverBack(toReviewState(document.back_file, null));
        } else {
          setFilePhotographicId(toReviewState(document.file, null));
        }
      }

      if (data.applicant_country?.value?.id === 14 && documents[1]) {
        hydrateAdditionalDocuments([
          ...(data.additional_documents || []),
          documents[1],
        ]);
      }

      if (data.applicant_country?.value?.id !== 14 && documents.length) {
        const first = documents[0];
        const second = documents[1];
        const firstType = getPhotoTypeValue(first, 1);
        const secondType = getPhotoTypeValue(second, 2);
        setPhotoIdFirst(firstType);
        setPhotoIdFirstOption(
          optionFromValue({ value: firstType }, photographicIdData),
        );
        setPhotoIdSecond(secondType);
        setPhotoIdSecondOption(
          optionFromValue({ value: secondType }, photographicIdData),
        );
        setPhotoTypeStateFirst(toReviewState(first.document_type, firstType));
        setPhotoTypeStateSecond(
          toReviewState(second?.document_type, secondType),
        );
        setPhotographicDocNames((previous) => ({
          ...previous,
          [String(safeId(first.id) ?? '')]:
            getReviewValue(first.document_type_name, '') ||
            'First photographic ID',
        }));
        if (second) {
          setPhotographicDocNames((previous) => ({
            ...previous,
            [String(safeId(second.id) ?? '')]:
              getReviewValue(second.document_type_name, '') ||
              'Second photographic ID',
          }));
        }
        setCountryCodeFirst(toReviewState(first.country_of_issue, null));
        setStateIdFirst(toReviewState(first.state_of_issue, null));
        setDocumentNumber(toReviewState(first.document_number, ''));
        setCardNumber(toReviewState(first.card_number, ''));
        setExpiryDate(toReviewState(first.expiry_date, ''));
        if (firstType === 2) {
          setFileDriverFrontFirst(toReviewState(first.file, null));
          setFileDriverBackFirst(toReviewState(first.back_file, null));
        } else {
          setFilePhotographicIdFirst(toReviewState(first.file, null));
        }
        if (second) {
          setCountryCodeSecond(toReviewState(second.country_of_issue, null));
          setStateIdSecond(toReviewState(second.state_of_issue, null));
          setDocumentNumberSecond(toReviewState(second.document_number, ''));
          setCardNumberSecond(toReviewState(second.card_number, ''));
          setExpiryDateSecond(toReviewState(second.expiry_date, ''));
          if (secondType === 2) {
            setFileDriverFrontSecond(toReviewState(second.file, null));
            setFileDriverBackSecond(toReviewState(second.back_file, null));
          } else {
            setFilePhotographicIdSecond(toReviewState(second.file, null));
          }
        }
      }

      if (data.non_photogenic_file) {
        setFileNonPhotographicId(toReviewState(data.non_photogenic_file, null));
        const nonPhotoValue = getReviewValue(data.non_photogenic_id_type, 1);
        setNonphotoId(nonPhotoValue);
        setNonphotoOption(
          optionFromValue(data.non_photogenic_id_type, nonPhotoData),
        );
      }
      if (data.non_photogenic_id_type !== undefined) {
        setNonPhotoTypeState(
          toReviewState(data.non_photogenic_id_type, nonphotoId),
        );
      }
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  useEffect(() => {
    getAllEnums();
    getApplicantCountry();
  }, []);

  useEffect(() => {
    if (stepStatus?.length > proofStepIndex && proofStatus !== 'DRAFT') {
      getDetails();
    }
  }, [stepStatus, updateFieldStatus]);

  const updateReview = (setter, value) => {
    setter((previous) => ({ ...previous, value }));
  };

  /* FIX (passport state_of_issue): a passport has no Australian state of
     issue — the state dropdown is only rendered for non-passport documents
     (driver's licence / photographic ID) issued in Australia. */
  const renderCountryAndState = (position, documentType) => {
    const currentCountry =
      position === 'primary'
        ? countryCode
        : position === 'first'
          ? countryCodeFirst
          : countryCodeSecond;
    const currentState =
      position === 'primary'
        ? stateId
        : position === 'first'
          ? stateIdFirst
          : stateIdSecond;
    const countryOption = optionFromValue(currentCountry);
    const stateOption = optionFromValue(currentState, stateData);
    const isPassportDocument = Number(documentType) === 1;

    return (
      <>
        <div className="mt-5">
          <LabelWithStatus
            id={`${position}-country`}
            label={fieldLabelMap.country_of_issue_id}
            required
            fieldData={currentCountry}
            handleStatusClick={handleStatusClick}
          />
          <CustomDropdownWithFetch
            label=""
            options={countryCodeData}
            search
            preselectedValue={countryOption}
            onSelect={(option) => selectCountry(option, position)}
            colorLabel=""
            fetchOptions={getCountries}
          />
        </div>
        {!isPassportDocument && currentCountry?.value?.id === 14 && (
          <div className="mt-4">
            <LabelWithStatus
              id={`${position}-state`}
              label={fieldLabelMap.state_of_issue}
              required
              fieldData={currentState}
              handleStatusClick={handleStatusClick}
            />
            <Dropdown
              label=""
              options={stateData}
              preselectedValue={stateOption}
              onSelect={(option) => selectState(option, position)}
              disabled={currentState?.status === 'approved'}
            />
          </div>
        )}
      </>
    );
  };

  /* Label row with the API-driven StatusButton for the document-type
     dropdowns (same pattern as LabelWithStatus). */
  const renderTypeLabel = (labelText, typeState, onOpen) => (
    <div className="mb-2 flex items-center justify-between gap-2">
      <span className="text-sm font-semibold text-slate-700">
        {labelText} <span className="text-rose-500">*</span>
      </span>
      <StatusButton
        status={typeState?.status}
        count={(typeState?.comments || []).length}
        openSection={onOpen}
      />
    </div>
  );

  const renderDocumentDetails = ({
    documentType,
    position,
    documentNumberState,
    cardNumberState,
    expiryState,
    photoFile,
    frontFile,
    backFile,
    photoRef,
    frontRef,
    backRef,
    setPhotoFile,
    setFrontFile,
    setBackFile,
  }) => {
    const isDriver = documentType === 2;
    const numberLabel = isDriver
      ? "Driver's License Number"
      : documentType === 1
        ? 'Passport Number'
        : 'Document Number';

    return (
      <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ReviewableInput
            id={`${position}-document-number`}
            label={numberLabel}
            required
            valueState={documentNumberState}
            onChange={(value) =>
              updateReview(
                position === 'second'
                  ? setDocumentNumberSecond
                  : setDocumentNumber,
                value,
              )
            }
            onStatusClick={handleStatusClick}
            placeholder="Enter document number"
            disabled={isProofLocked}
          />
          {isDriver && (
            <ReviewableInput
              id={`${position}-card-number`}
              label="Card Number"
              required
              valueState={cardNumberState}
              onChange={(value) =>
                updateReview(
                  position === 'second' ? setCardNumberSecond : setCardNumber,
                  value,
                )
              }
              onStatusClick={handleStatusClick}
              placeholder="Enter card number"
              disabled={isProofLocked}
            />
          )}
          <div className="mt-4">
            <LabelWithStatus
              id={`${position}-expiry`}
              label="Expiry Date"
              required
              fieldData={expiryState}
              handleStatusClick={handleStatusClick}
            />
            <ThemedDatePicker
              selected={toDate(expiryState.value)}
              onChange={(date) =>
                updateReview(
                  position === 'second' ? setExpiryDateSecond : setExpiryDate,
                  date,
                )
              }
              disabled={isProofLocked || expiryState.status === 'approved'}
              dateFormat="dd/MM/yyyy"
              minDate={new Date()}
              showMonthDropdown
              showYearDropdown
              dropdownMode="scroll"
              scrollableYearDropdown
              yearDropdownItemNumber={100}
              placeholderText="Select expiry date"
              invalid={expiryState.status === 'rejected'}
              wrapperClassName="mt-1 w-full"
              className="w-full"
            />
          </div>
        </div>

        {isDriver ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <FileUploadBox
              label="Driver's License Front"
              required
              fileState={frontFile}
              inputRef={frontRef}
              onChange={(event) =>
                uploadFileToState(
                  event.target.files?.[0],
                  setFrontFile,
                  frontRef,
                )
              }
              onDelete={() => clearFile(setFrontFile, frontRef)}
              onPreview={handleFilePreview}
              disabled={isProofLocked}
            />
            <FileUploadBox
              label="Driver's License Back"
              required
              fileState={backFile}
              inputRef={backRef}
              onChange={(event) =>
                uploadFileToState(event.target.files?.[0], setBackFile, backRef)
              }
              onDelete={() => clearFile(setBackFile, backRef)}
              onPreview={handleFilePreview}
              disabled={isProofLocked}
            />
          </div>
        ) : (
          <FileUploadBox
            label={
              documentType === 1 ? 'Passport copy' : 'Photographic ID copy'
            }
            required
            fileState={photoFile}
            inputRef={photoRef}
            onChange={(event) =>
              uploadFileToState(event.target.files?.[0], setPhotoFile, photoRef)
            }
            onDelete={() => clearFile(setPhotoFile, photoRef)}
            onPreview={handleFilePreview}
            onStatusClick={handleStatusClick}
            disabled={isProofLocked}
          />
        )}
        {renderCountryAndState(position, documentType)}
      </div>
    );
  };

  const isEditable = !isProofLocked;
  const rejectedFieldLabels = {
    has_prosecution_history: 'Prosecution history',
    prosecution_description: 'Description',
    prosecution_file: 'Prosecution proof',
    photographic_documents: 'Photographic documents',
    non_photogenic_id_type: 'Non-photographic ID type',
    non_photogenic_file: 'Non-photographic ID',
    country_of_issue_id: 'Country where issued',
    state_of_issue: 'State or Territory in Australia',
  };

  /* FIX: map the rejected-fields API response to a display label per
     field. The API returns the section under PROOF_DOCUMENTS with dotted
     document paths such as "photographic_documents.114.document_number",
     plus top-level fields such as "non_photogenic_file" and
     "has_prosecution_history". The legacy UPLOAD_PROOF_DOCS key is still
     supported. */
  const buildRejectedFieldChips = (rawRejectedFields) => {
    const section =
      rawRejectedFields?.PROOF_DOCUMENTS ??
      rawRejectedFields?.UPLOAD_PROOF_DOCS ??
      {};
    return (section.rejected_fields || []).map((field) => {
      const path = String(field);
      const documentMatch = path.match(
        /^(photographic_documents|additional_documents)\.(\d+)\.(.+)$/,
      );
      if (documentMatch) {
        const [, , documentId, fieldName] = documentMatch;
        /* FIX: resolve the document from what is actually rendered on
           this page — a document returned under photographic_documents
           can be displayed inside the additional-documents section
           (e.g. an AU driving licence merged from
           photographic_documents). Check the additional documents first
           so the chip shows the additional document's details (its type
           label) instead of a raw "Document <id>". */
        const documentName =
          additionalDocuments.find(
            (document) => String(document.serverId) === documentId,
          )?.documentType?.label ||
          photographicDocNames?.[documentId] ||
          `Document ${documentId}`;
        return {
          key: path,
          label: `${documentName} · ${
            REJECTED_DOCUMENT_FIELD_LABELS[fieldName] ||
            fieldName.replace(/_/g, ' ')
          }`,
        };
      }
      return {
        key: path,
        label:
          rejectedFieldLabels[path] ||
          path.replace(/[._]/g, ' ').replace(/\s+/g, ' ').trim(),
      };
    });
  };
  const rejectedFieldChips = buildRejectedFieldChips(rejectedFields);

  return (
    <div className="flex min-h-[calc(100vh-100px)] flex-col w-full max-w-full min-w-0 overflow-x-hidden">
      {loading && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-950/50 backdrop-blur-sm">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-white/30 border-t-white" />
        </div>
      )}

      {['IN_REVIEW', 'COMPLETED'].includes(proofStatus) && (
        <div className="mb-4 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="text-lg">ⓘ</span>
          <span>
            This section is <strong>in review by our onboarding team</strong>.
            You will be notified once verification is complete.
          </span>
        </div>
      )}

      {proofStatus === 'APPROVED' && (
        <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          This section has been <strong>approved</strong> by our onboarding
          team.
        </div>
      )}

      {rejectedFieldChips.length > 0 && (
        <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 p-4">
          <p className="mb-2 text-sm font-bold text-rose-700">
            {rejectedFieldChips.length === 1
              ? 'The following field requires correction:'
              : 'The following fields require correction:'}{' '}
          </p>
          <div className="flex flex-wrap gap-2">
            {rejectedFieldChips.map((chip) => (
              <span
                key={chip.key}
                className="rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-700"
              >
                {chip.label}
              </span>
            ))}
          </div>
        </div>
      )}

      <main className="flex-1 space-y-5">
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_16px_50px_rgba(30,41,59,0.08)]">
          <div className="border-b border-slate-100  px-5 py-6 sm:px-7">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#375AE7] text-xl text-white shadow-lg shadow-blue-200">
                ✓
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                  Upload proof documents
                </h1>
                <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
                  Upload clear colour copies of the required documents. If you
                  make changes to an earlier section, use Reset Application
                  before resubmitting.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 p-5 sm:p-7 lg:grid-cols-2">
            <div>
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-bold text-slate-800">
                    Current or recent prosecutions and inquiries
                  </p>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Money laundering, terrorist links, tax offences or
                    corruption related to the individual.
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <StatusButton
                    status={signatory.status}
                    count={(signatory.comments || []).length}
                    openSection={() =>
                      handleStatusClick(
                        signatory,
                        'Prosecution history',
                        signatory.value,
                        '',
                      )
                    }
                  />
                  <span className="text-rose-500">*</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-5">
                {[true, false].map((value) => (
                  <label
                    key={String(value)}
                    className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700"
                  >
                    <input
                      type="radio"
                      name="prosecution-history"
                      checked={signatory.value === value}
                      disabled={!isEditable}
                      onChange={() => {
                        updateReview(setSignatory, value);
                        if (!value) {
                          setDescription(createReviewState(''));
                          clearFile(setFileProof, fileProofRef);
                        }
                      }}
                      className="h-4 w-4 accent-[#375AE7]"
                    />
                    {value ? 'Yes' : 'No'}
                  </label>
                ))}
              </div>

              {signatory.value === true && (
                <>
                  <div className="mt-5">
                    <LabelWithStatus
                      id="prosecution-description"
                      label="Description"
                      required
                      fieldData={description}
                      handleStatusClick={handleStatusClick}
                    />
                    <textarea
                      id="prosecution-description"
                      rows={5}
                      value={description.value || ''}
                      disabled={
                        !isEditable || description.status === 'approved'
                      }
                      onChange={(event) =>
                        updateReview(setDescription, event.target.value)
                      }
                      className="mt-1 w-full resize-y rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#375AE7] focus:ring-4 focus:ring-[#375AE7]-100"
                      placeholder="Add description..."
                    />
                  </div>
                  <FileUploadBox
                    label="Upload proof"
                    required
                    fileState={fileProof}
                    inputRef={fileProofRef}
                    onChange={(event) =>
                      uploadFileToState(
                        event.target.files?.[0],
                        setFileProof,
                        fileProofRef,
                      )
                    }
                    onDelete={() => clearFile(setFileProof, fileProofRef)}
                    onPreview={handleFilePreview}
                    onStatusClick={handleStatusClick}
                    disabled={!isEditable}
                  />
                </>
              )}
            </div>

            <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4 sm:p-5">
              <p className="text-xs font-bold uppercase tracking-[0.13em] text-slate-500">
                Verification checklist
              </p>
              <ul className="mt-3 space-y-3 text-sm leading-6 text-slate-600">
                <li className="flex flex-wrap gap-2">
                  <span className="text-[#375AE7]">●</span>
                  Submit colour copies of each document.
                </li>
                <li className="flex flex-wrap gap-2">
                  <span className="text-[#375AE7]">●</span>
                  Make sure names and numbers are readable.
                </li>
                <li className="flex flex-wrap gap-2">
                  <span className="text-[#375AE7]">●</span>
                  Additional documents can be selected below when needed.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_16px_50px_rgba(30,41,59,0.06)] sm:p-7">
          <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.13em] text-[#375AE7]">
                Primary identity documents
              </p>
              <h2 className="mt-1 text-xl font-bold tracking-tight text-slate-900">
                {applicantCountry === 14
                  ? 'Australian applicant document'
                  : 'Applicant documents'}
              </h2>
            </div>
            <p className="text-xs text-slate-400">
              Fields marked * are required
            </p>
          </div>

          {applicantCountry === 14 ? (
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 p-4 sm:p-5">
                {renderTypeLabel('Photographic ID', photoTypeState, () =>
                  handleStatusClick(
                    photoTypeState,
                    'Photographic ID',
                    photoTypeState.value,
                    '',
                  ),
                )}
                <Dropdown
                  label=""
                  options={photographicIdData}
                  preselectedValue={photoIdOption}
                  onSelect={(option) => setPhotoDocumentType(option, 'primary')}
                  disabled={!isEditable}
                />
                {renderDocumentDetails({
                  documentType: photoId,
                  position: 'primary',
                  documentNumberState: documentNumber,
                  cardNumberState: cardNumber,
                  expiryState: expiryDate,
                  photoFile: filePhotographicId,
                  frontFile: fileDriverFront,
                  backFile: fileDriverBack,
                  photoRef: filePhotoRef,
                  frontRef: fileFrontRef,
                  backRef: fileBackRef,
                  setPhotoFile: setFilePhotographicId,
                  setFrontFile: setFileDriverFront,
                  setBackFile: setFileDriverBack,
                })}
              </div>

              {photoId !== 2 && (
                <div className="rounded-2xl border border-slate-200 p-4 sm:p-5">
                  {renderTypeLabel(
                    'Non-photographic ID',
                    nonPhotoTypeState,
                    () =>
                      handleStatusClick(
                        nonPhotoTypeState,
                        'Non-photographic ID',
                        nonPhotoTypeState.value,
                        '',
                      ),
                  )}
                  <Dropdown
                    label=""
                    options={nonPhotoData}
                    preselectedValue={nonphotoOption}
                    onSelect={(option) => {
                      setNonphotoId(option.value);
                      setNonphotoOption(option);
                      updateReview(setNonPhotoTypeState, option.value);
                      clearFile(setFileNonPhotographicId, fileNonPhotoRef);
                    }}
                    disabled={!isEditable}
                  />
                  <FileUploadBox
                    label="Non-photographic ID document"
                    required
                    fileState={fileNonPhotographicId}
                    inputRef={fileNonPhotoRef}
                    onChange={(event) =>
                      uploadFileToState(
                        event.target.files?.[0],
                        setFileNonPhotographicId,
                        fileNonPhotoRef,
                      )
                    }
                    onDelete={() =>
                      clearFile(setFileNonPhotographicId, fileNonPhotoRef)
                    }
                    onPreview={handleFilePreview}
                    onStatusClick={handleStatusClick}
                    disabled={!isEditable}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 p-4 sm:p-5">
                {renderTypeLabel(
                  'First photographic ID',
                  photoTypeStateFirst,
                  () =>
                    handleStatusClick(
                      photoTypeStateFirst,
                      'First photographic ID',
                      photoTypeStateFirst.value,
                      '',
                    ),
                )}
                <Dropdown
                  label=""
                  options={photographicIdData}
                  preselectedValue={photoIdFirstOption}
                  onSelect={(option) => setPhotoDocumentType(option, 'first')}
                  disabled={!isEditable}
                />
                {renderDocumentDetails({
                  documentType: photoIdFirst,
                  position: 'first',
                  documentNumberState: documentNumber,
                  cardNumberState: cardNumber,
                  expiryState: expiryDate,
                  photoFile: filePhotographicIdFirst,
                  frontFile: fileDriverFrontFirst,
                  backFile: fileDriverBackFirst,
                  photoRef: filePhotoFirstRef,
                  frontRef: fileFrontFirstRef,
                  backRef: fileBackFirstRef,
                  setPhotoFile: setFilePhotographicIdFirst,
                  setFrontFile: setFileDriverFrontFirst,
                  setBackFile: setFileDriverBackFirst,
                })}
              </div>

              <div className="rounded-2xl border border-slate-200 p-4 sm:p-5">
                {renderTypeLabel(
                  'Second photographic ID',
                  photoTypeStateSecond,
                  () =>
                    handleStatusClick(
                      photoTypeStateSecond,
                      'Second photographic ID',
                      photoTypeStateSecond.value,
                      '',
                    ),
                )}
                <Dropdown
                  label=""
                  options={photographicIdData}
                  preselectedValue={photoIdSecondOption}
                  onSelect={(option) => setPhotoDocumentType(option, 'second')}
                  disabled={!isEditable}
                />
                {renderDocumentDetails({
                  documentType: photoIdSecond,
                  position: 'second',
                  documentNumberState: documentNumberSecond,
                  cardNumberState: cardNumberSecond,
                  expiryState: expiryDateSecond,
                  photoFile: filePhotographicIdSecond,
                  frontFile: fileDriverFrontSecond,
                  backFile: fileDriverBackSecond,
                  photoRef: filePhotoSecondRef,
                  frontRef: fileFrontSecondRef,
                  backRef: fileBackSecondRef,
                  setPhotoFile: setFilePhotographicIdSecond,
                  setFrontFile: setFileDriverFrontSecond,
                  setBackFile: setFileDriverBackSecond,
                })}
              </div>
            </div>
          )}
        </section>

        <AdditionalDocumentSection
          options={additionalDocumentOptions}
          documents={additionalDocuments}
          onAddDocument={addAdditionalDocument}
          onRemoveDocument={removeAdditionalDocument}
          onDocumentTypeChange={handleSelectAdditionalDocumentType}
          onFieldChange={handleAdditionalFieldChange}
          onFileSelected={handleAdditionalFileSelected}
          onRemoveFile={clearAdditionalDocumentFile}
          onPreviewFile={handleFilePreview}
          onStatusClick={handleAdditionalStatusClick}
          countryOptions={countryCodeData}
          stateOptions={stateData}
          fetchCountries={getCountries}
          disabled={isProofLocked}
        />
      </main>

      {isModalOpen && currentDocument && (
        <DocumentPreview
          fileUrl={currentDocument.presigned_url}
          fileName={currentDocument.file_name}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      {showPopup && (
        <ReviewPopup
          isOpen={showPopup}
          currentField={currentField}
          currentFieldName={currentFieldName}
          currentFieldValue={currentFieldValue}
          currentFieldType={currentFieldType}
          onClose={() => setShowPopup(false)}
        />
      )}

      <footer className="mt-6 sm:mt-8 flex flex-col gap-3 sm:gap-4 rounded-2xl bg-[#375AE7] p-3 sm:p-4 sm:flex-row sm:items-center sm:justify-between sticky bottom-2 z-20 md:static">
        <ConfirmResetButton
          onConfirm={handleReset}
          isReset={!isProofLocked}
          isBack
          onBack={() =>
            setCurrentStep(
              !isInvitee && !isInviteeDirector && individualAccountType == 5
                ? 3
                : 2,
            )
          }
        />

        {!isProofLocked ? (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end w-full gap-2 sm:gap-3 md:gap-4 relative">
            {/* Save as Draft */}
            <button
              onClick={() => {
                handleContinue(false);
              }}
              className="flex w-full sm:w-auto min-h-[44px] items-center justify-center space-x-2 border-2 border-white text-white font-medium py-2 px-4 rounded-full text-sm"
            >
              <span>Save as Draft</span>
            </button>

            <ConfirmContinueButton
              onContinue={() => handleContinue(true)}
              buttonText="Continue"
              confirmTitle="Save Section"
              confirmMessage="Are you sure, you want to continue. This action will ensure, the saved information is un-editable. Therefore please make sure you have provided the correct details."
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() =>
              setCurrentStep(
                !isInvitee && !isInviteeDirector && individualAccountType == 5
                  ? 5
                  : 4,
              )
            }
            className="rounded-full border border-white/70 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
          >
            Next
          </button>
        )}
      </footer>
    </div>
  );
};

export default UploadProof;
